import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Users, Home, Compass } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { IMG } from "../config";

const DUSUN = [
  {
    id: "krajan",
    num: 1,
    name: "Dusun Krajan",
    tagline: "Jantung Pemerintahan & Kreasi Pemuda",
    jiwa: "412 jiwa",
    rtrw: "6 RT · 2 RW",
    spot: "Halaman Balai Desa — panggung gigs & nobar wota tiap malam minggu",
    desc: "Pusat pemerintahan sekaligus ruang ekspresi anak muda. Saat akhir pekan, halaman balai desa berubah jadi panggung terbuka: band latihan, wota nobar bareng, dan kreator konten syuting tanpa takut diusir.",
    img: IMG.wota,
    alt: "Malam pemuda dengan lightstick warna-warni di halaman balai desa",
    marker: { x: 285, y: 195 },
  },
  {
    id: "cempaka",
    num: 2,
    name: "Dusun Cempaka",
    tagline: "Lumbung Padi & Kolam Sedulur",
    jiwa: "386 jiwa",
    rtrw: "5 RT · 2 RW",
    spot: "Kolam Mancing Warga — markas Mancing Mania Cianjay di tepi pematang",
    desc: "Dusun agraris yang hidup dari sawah dan kolam warga. Saluran irigasinya baru saja dinormalisasi, dan kolamnya jadi titik kumpul sore paling rame — tempat curhat sambil menunggu umpan disambar.",
    img: IMG.mancing,
    alt: "Warga berbincang santai di tepi kolam pemancingan dusun",
    marker: { x: 165, y: 330 },
  },
  {
    id: "rejosari",
    num: 3,
    name: "Dusun Rejosari",
    tagline: "Gardu Terdepan Jaga Malam",
    jiwa: "358 jiwa",
    rtrw: "5 RT · 1 RW",
    spot: "Pos Ronda Bambu — kedai kopi warga yang hangat sampai larut",
    desc: "Pintu masuk utama Cianjay dari arah kecamatan. Pos ronda bambunya ikonik: lampu pijar hangat, kopi tubruk, dan warga yang bergantian berjaga — titik favorit Pak Kades menjemput aspirasi tengah malam.",
    img: IMG.ronda,
    alt: "Suasana malam hangat di pos ronda bambu Dusun Rejosari",
    marker: { x: 445, y: 115 },
  },
];

const BLOBS = {
  rejosari: "M380,42 C470,18 560,62 570,142 C580,222 500,232 438,210 C376,190 350,102 380,42 Z",
  krajan: "M180,122 C262,90 362,122 372,202 C382,272 300,302 218,272 C148,246 118,162 180,122 Z",
  cempaka: "M62,282 C142,252 262,292 272,352 C282,402 182,422 102,397 C32,374 12,317 62,282 Z",
};

const MapSvg = ({ active, onSelect }) => (
  <svg viewBox="0 0 600 440" className="w-full h-auto" role="img" aria-label="Peta ilustrasi Desa Cianjay">
    <path d="M0,332 C120,300 225,362 345,330 S520,268 600,302 L600,440 L0,440 Z" className="dusun-land" />
    <path d="M0,332 C120,300 225,362 345,330 S520,268 600,302" fill="none" className="dusun-river" strokeWidth="3" strokeLinecap="round" />
    {DUSUN.map((d) => (
      <path
        key={d.id}
        d={BLOBS[d.id]}
        className={`dusun-blob ${active === d.id ? "active" : ""}`}
        strokeWidth="2"
        onClick={() => onSelect(d.id)}
        data-testid={`map-area-${d.id}`}
      />
    ))}
    <path d="M300,20 C295,90 290,140 285,195 M285,195 C260,250 210,295 165,330 M285,195 C345,165 400,140 445,115" fill="none" className="dusun-road" strokeWidth="2.5" strokeDasharray="7 7" strokeLinecap="round" />
    {DUSUN.map((d) => (
      <text key={`label-${d.id}`} x={d.marker.x} y={d.marker.y - 34} textAnchor="middle" className="fill-soft font-mono" fontSize="11" letterSpacing="2">
        {d.name.replace("Dusun ", "").toUpperCase()}
      </text>
    ))}
    {DUSUN.map((d) => (
      <g key={`marker-${d.id}`} onClick={() => onSelect(d.id)} className="cursor-pointer" data-testid={`map-marker-${d.id}`}>
        <circle cx={d.marker.x} cy={d.marker.y} r="26" className={`fill-terra ${active === d.id ? "opacity-25 animate-glow-dot" : "opacity-0"}`} />
        <circle cx={d.marker.x} cy={d.marker.y} r="16" className={`transition-all duration-300 stroke-surface ${active === d.id ? "fill-terra" : "fill-forest"}`} strokeWidth="3" />
        <text x={d.marker.x} y={d.marker.y + 5} textAnchor="middle" fontSize="14" fontWeight="700" className="fill-[#F7F4EE]" fontFamily="Playfair Display, serif">
          {d.num}
        </text>
      </g>
    ))}
    <text x="505" y="262" className="fill-soft/70 font-mono" fontSize="10" letterSpacing="1.5" transform="rotate(-8 505 262)">KALI CIPANDAK</text>
  </svg>
);

export const DusunGallery = () => {
  const [active, setActive] = useState("krajan");
  const d = DUSUN.find((x) => x.id === active);

  return (
    <section id="dusun" data-testid="dusun-section" className="py-24 sm:py-32 bg-surfacewarm border-y border-line transition-colors duration-500">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          bab="Wilayah"
          label="Galeri Dusun"
          title="Tiga Dusun, Satu Denyut Cianjay"
          sub="Klik tiap penanda di peta untuk berkenalan dengan karakter, warga, dan spot andalan masing-masing dusun."
          testid="dusun"
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 rounded-[2rem] border border-line bg-surface p-5 sm:p-8 shadow-[0_14px_40px_rgba(30,58,43,0.08)]"
            data-testid="dusun-map"
          >
            <MapSvg active={active} onSelect={setActive} />
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-soft">
              Peta ilustrasi · tidak berskala
            </p>
          </motion.div>

          <div className="lg:col-span-6">
            <div className="flex flex-wrap gap-2.5 mb-6">
              {DUSUN.map((x) => (
                <button
                  key={x.id}
                  data-testid={`dusun-tab-${x.id}`}
                  onClick={() => setActive(x.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                    active === x.id
                      ? "bg-forest text-bg shadow-[0_10px_25px_rgba(30,58,43,0.25)]"
                      : "border border-line bg-surface text-ink/70 hover:border-terra hover:text-terra"
                  }`}
                >
                  {x.num}. {x.name.replace("Dusun ", "")}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.article
                key={d.id}
                data-testid={`dusun-card-${d.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden rounded-[2rem] border border-line bg-surface shadow-[0_24px_60px_rgba(30,58,43,0.14)]"
              >
                <div className="relative h-56 sm:h-64 overflow-hidden">
                  <img src={d.img} alt={d.alt} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E1712]/90 via-[#0E1712]/25 to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-[#F7F4EE]/90 backdrop-blur px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#1E3A2B]">
                    Dusun {d.num} dari 3
                  </span>
                  <div className="absolute bottom-4 left-5 right-5">
                    <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#F7F4EE]">{d.name}</h3>
                    <p className="mt-1 text-sm text-[#E3B583] italic font-serif">{d.tagline}</p>
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full bg-badge px-4 py-2 text-xs font-semibold" data-testid={`dusun-stat-jiwa-${d.id}`}>
                      <Users size={13} className="text-terra" /> {d.jiwa}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-badge px-4 py-2 text-xs font-semibold" data-testid={`dusun-stat-rtrw-${d.id}`}>
                      <Home size={13} className="text-terra" /> {d.rtrw}
                    </span>
                  </div>
                  <p className="mt-5 text-sm sm:text-base leading-relaxed text-soft">{d.desc}</p>
                  <div className="mt-6 flex items-start gap-3.5 rounded-2xl border border-gold/50 bg-gold/10 px-5 py-4" data-testid={`dusun-spot-${d.id}`}>
                    <Compass size={18} className="mt-0.5 shrink-0 text-terra" />
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft">Spot Andalan Warga</p>
                      <p className="mt-1 text-sm font-semibold leading-snug">{d.spot}</p>
                    </div>
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
